import { Card, CardContent, Grid } from '@mui/material'
import PropTypes from 'prop-types'
import React from 'react'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import i18n from 'simple-react-i18n'
import { countBy, groupBy, keys, omit, toInteger } from 'lodash'
import { IAEAU_SCALE_COLORS, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import CatchmentDto from 'catchment/dto/CatchmentDto'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import QualityAction from 'quality/actions/QualityAction'
import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'
import { STATION_TYPE } from 'station/constants/StationConstants'

const buildOptions = (data) => {
    const formatTooltip = ({ marker, data: { name = '' } }) => `${marker} ${name}`
    return {
        tooltip: {
            trigger: 'item',
            formatter: formatTooltip,
        },
        legend: {
            orient: 'vertical',
            top: 'center',
            right: '10%',
            itemWidth: 28,
            itemHeight: 17,
            textStyle: {
                width: 300,
                overflow: 'break',
                fontSize: 13,
                padding: [0, 0, 0, 10],
            },
        },
        series: [
            {
                type: 'pie',
                radius: ['35%', '80%'],
                label: {
                    show: false,
                },
                data,
                right: '60%',
            },
        ],
    }
}

const sortCatchmentByPriorityAndSensibility = (catchment = {}) => {
    if (catchment.priority && catchment.sensitive) return `${i18n.havePriority} ${i18n.and} ${i18n.sensitive}`
    if (catchment.priority) return i18n.havePriority
    if (catchment.sensitive) return i18n.sensitive
    return i18n.notDetermined
}

const getIndicatorColor = (key) => {
    switch (key) {
        case i18n.havePriority : return PASTEL_COLOR.red
        case i18n.sensitive : return PASTEL_COLOR.orange
        case `${i18n.havePriority} ${i18n.and} ${i18n.sensitive}` : return PASTEL_COLOR.yellow
        case i18n.undergroundWater : return IAEAU_SCALE_COLORS.EXCELLENT
        case i18n.surfaceWater : return IAEAU_SCALE_COLORS.GOOD
        default: return PASTEL_COLOR.grey
    }
}

const orderIndicator = (key) => {
    switch (key) {
        case i18n.havePriority : return 3
        case i18n.sensitive : return 2
        case `${i18n.havePriority} ${i18n.and} ${i18n.sensitive}` : return 1
        default: return 4
    }
}

const CatchmentsTypeChart = ({
    catchments = [],
}) => {
    const groupedIndicators = groupBy(catchments, sortCatchmentByPriorityAndSensibility)

    const formattedData = keys(groupedIndicators).sort((a, b) => orderIndicator(a) > orderIndicator(b) ? 1 : -1).map(key => {
        const data = groupedIndicators[key]
        return {
            value: data.length,
            name: `${key} (${data.length})`,
            itemStyle: {
                color: getIndicatorColor(key),
            },
        }
    })

    const option = buildOptions(formattedData)

    return (
        <ReactECharts
            echarts={echarts}
            option={option}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: '150px' }}
        />
    )
}

CatchmentsTypeChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
}

const sortQualitometersByResource = (qualitometer = {}) => {
    const stationType = toInteger(qualitometer.stationType)
    switch (stationType) {
        case STATION_TYPE.POINT_EAU_SOUTERRAINE:
        case STATION_TYPE.SOURCE:
            return i18n.undergroundWater
        case STATION_TYPE.PLAN_D_EAU:
        case STATION_TYPE.COURS_D_EAU:
        case STATION_TYPE.GRAND_COURS_D_EAU:
        case STATION_TYPE.AUTRE_COURS_D_EAU:
        case STATION_TYPE.MILIEU_MARIN:
            return i18n.surfaceWater
        default: return i18n.notDetermined
    }
}

const CatchmentsStationsTypeChart = ({
    catchments = [],
    catchmentPoints = [],
}) => {
    const {
        qualitometers,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const catchmentIds = catchments.map(c => c.id)
    const linkedCatchmentPoints = catchmentPoints.filter(p => catchmentIds.includes(p.id)).map(p => p.codeWithoutDesignation)
    const linkedQualitometers = qualitometers.filter(q => linkedCatchmentPoints.includes(q.code))
    const groupedIndicators = groupBy(linkedQualitometers, sortQualitometersByResource)

    const formattedData = keys(groupedIndicators).map(key => {
        const data = groupedIndicators[key]
        return {
            value: data.length,
            name: `${key} (${data.length})`,
            itemStyle: {
                color: getIndicatorColor(key),
            },
        }
    })

    const option = buildOptions(formattedData)

    return (
        <ReactECharts
            echarts={echarts}
            option={option}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: '150px', maxWidth: '500px' }}
        />
    )
}

CatchmentsStationsTypeChart.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
}

const CatchmentKeyFigures = ({
    catchments = [],
}) => {
    const countPriorities = omit(countBy(catchments, sortCatchmentByPriorityAndSensibility), i18n.notDetermined)
    const displayEveryPriority = {
        [`${i18n.havePriority} ${i18n.and} ${i18n.sensitive}`]: countPriorities[`${i18n.havePriority} ${i18n.and} ${i18n.sensitive}`] ?? 0,
        [i18n.havePriority]: countPriorities[i18n.havePriority] ?? 0,
        [i18n.sensitive]: countPriorities[i18n.sensitive] ?? 0,
    }

    return (
        <Grid container alignItems='center' sx={{ height: '100%', fontWeight: '600' }}>
            <Grid size={10}>
                {i18n.catchments}
            </Grid>
            <Grid size={2}>
                {catchments.length}
            </Grid>
            {keys(displayEveryPriority).sort((a, b) => orderIndicator(a) > orderIndicator(b) ? 1 : -1).map(priority => (
                <React.Fragment key={priority}>
                    <Grid size={10}>
                        {priority}
                    </Grid>
                    <Grid size={2} >
                        {displayEveryPriority[priority]}
                    </Grid>
                </React.Fragment>
            ))}
        </Grid>
    )
}

CatchmentKeyFigures.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
}

const CatchmentsIndicators = ({
    catchments = [],
    catchmentPoints = [],
}) => {
    const dispatch = useDispatch()

    const { isLoaded: isQualitoLoaded } = useProgressDispatch(() => [
        dispatch(QualityAction.fetchQualitometersLight()),
    ], [])

    const dispatchScreen = window.innerWidth >= 1280 ? 0 : 1
    return (
        <Card>
            <CardContent>
                <Grid container spacing={2}>
                    <Grid size={5 + (isQualitoLoaded ? dispatchScreen : 2)} offset={isQualitoLoaded ? 0 : 1}>
                        <StyledFieldSet sx={{ maxWidth: '500px', margin: 0, padding: '0 0 0 1rem' }}>
                            <StyledLegend>{i18n.catchmentType}</StyledLegend>
                            <CatchmentsTypeChart catchments={catchments} />
                        </StyledFieldSet>
                    </Grid>
                    {isQualitoLoaded && (
                        <Grid size={5 + dispatchScreen}>
                            <StyledFieldSet sx={{ maxWidth: '500px', margin: 0, padding: '0 0 0 1rem' }}>
                                <StyledLegend>{i18n.resources}</StyledLegend>
                                <CatchmentsStationsTypeChart catchments={catchments} catchmentPoints={catchmentPoints} />
                            </StyledFieldSet>
                        </Grid>
                    )}
                    {dispatchScreen === 0 && (
                        <Grid size={2 + (isQualitoLoaded ? 0 : 1)} offset={isQualitoLoaded ? 0 : 1}>
                            <StyledFieldSet sx={{ height: '100%', margin: 0, padding: '0 0 0 1rem' }}>
                                <StyledLegend>{i18n.keyFigures}</StyledLegend>
                                <CatchmentKeyFigures catchments={catchments} />
                            </StyledFieldSet>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    )
}

CatchmentsIndicators.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
}

export default CatchmentsIndicators